import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { fetchSession } from '../utils/authClient';

const ProfileContainer = styled.div`
  min-height: 100vh;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const ProfileHeader = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 20px;
  padding: 3rem;
  margin-bottom: 2rem;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(45deg, #00ff88, #00d4ff);
    opacity: 0.1;
  }
`;

const ProfileCover = styled.div`
  height: 200px;
  background: linear-gradient(135deg, #00ff88, #00d4ff);
  border-radius: 15px;
  margin: -3rem -3rem 2rem -3rem;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2rem;
  font-weight: bold;
`;

const ProfileInfo = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const ProfilePhotoSection = styled.div`
  position: relative;
`;

const ProfilePhoto = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: ${props => props.imageUrl ? `url(${props.imageUrl}) center/cover` : 'linear-gradient(45deg, #00ff88, #00d4ff)'};
  border: 4px solid #00ff88;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: ${props => props.imageUrl ? 'transparent' : 'white'};
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 10px 30px rgba(0, 255, 136, 0.3);
  }
`;

const PhotoUploadOverlay = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  background: #00ff88;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid #0a0a0a;

  &:hover {
    transform: scale(1.1);
    background: #00cc6a;
  }
`;

const ProfileDetails = styled.div`
  flex: 1;
`;

const ProfileName = styled.h1`
  font-size: 2.5rem;
  color: #ffffff;
  margin-bottom: 0.5rem;
  font-weight: bold;
`;

const ProfileUsername = styled.p`
  color: #00ff88;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-weight: 500;
`;

const ProfileBio = styled.p`
  color: #cccccc;
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 600px;
`;

const ProfileStats = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #00ff88;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #cccccc;
  margin-top: 0.25rem;
`;

const ProfileContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ProfileSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 15px;
  padding: 2rem;
  backdrop-filter: blur(10px);
`;

const SectionTitle = styled.h2`
  color: #00ff88;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InfoGrid = styled.div`
  display: grid;
  gap: 1rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: #cccccc;
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: #ffffff;
  font-weight: 600;
`;

const EditButton = styled.button`
  background: linear-gradient(45deg, #00ff88, #00cc6a);
  color: #000000;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 255, 136, 0.3);
  }
`;

const ActionButton = styled(Link)`
  display: inline-block;
  background: linear-gradient(45deg, #00ff88, #00cc6a);
  color: #000000;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  text-decoration: none;
  font-weight: bold;
  transition: all 0.3s ease;
  text-align: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 255, 136, 0.3);
    text-decoration: none;
    color: #000000;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const ConnectedAccounts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const AccountCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 255, 136, 0.2);
  border-radius: 10px;
  padding: 1rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: #00ff88;
    background: rgba(255, 255, 255, 0.05);
  }
`;

const AccountName = styled.div`
  font-weight: bold;
  color: #ffffff;
  margin-bottom: 0.25rem;
`;

const AccountDetails = styled.div`
  font-size: 0.9rem;
  color: #cccccc;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: bold;
  text-transform: uppercase;

  &.active {
    background: rgba(0, 255, 136, 0.2);
    color: #00ff88;
  }

  &.pending {
    background: rgba(255, 193, 7, 0.2);
    color: #ffc107;
  }

  &.inactive {
    background: rgba(255, 87, 87, 0.2);
    color: #ff5757;
  }
`;

const Profile = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(null);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const session = await fetchSession();
      if (session.network || session.error) {
        setError(session.message || 'Unable to load account.');
        return;
      }
      if (session.status === 401) {
        navigate('/signin', { state: { from: '/profile' } });
        return;
      }
      if (session.status === 403) {
        setDenied(session.message || 'This account cannot view the profile yet.');
        return;
      }
      setUserData(session.user);
    } catch (err) {
      setError('Unable to load profile.');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const getInitials = () => {
    if (!userData?.firstName || !userData?.lastName) return '?';
    return `${userData.firstName.charAt(0)}${userData.lastName.charAt(0)}`.toUpperCase();
  };

  if (loading) {
    return (
      <ProfileContainer>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ color: '#00ff88', fontSize: '2rem' }}>Loading...</div>
        </div>
      </ProfileContainer>
    );
  }

  if (error) {
    return (
      <ProfileContainer>
        <div style={{ textAlign: 'center', padding: '3rem', color: '#ff8888' }}>{error}</div>
      </ProfileContainer>
    );
  }

  if (denied) {
    return (
      <ProfileContainer>
        <div style={{ textAlign: 'center', padding: '3rem', color: '#cccccc' }}>{denied}</div>
      </ProfileContainer>
    );
  }

  if (!userData) {
    return (
      <ProfileContainer>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ color: '#00ff88', fontSize: '2rem' }}>Loading...</div>
        </div>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer>
      <ProfileHeader>
        <ProfileCover>
          <span>Skylink Profile</span>
        </ProfileCover>
        
        <ProfileInfo>
          <ProfilePhotoSection>
            <ProfilePhoto imageUrl={profileImage}>
              {!profileImage && getInitials()}
            </ProfilePhoto>
            <PhotoUploadOverlay onClick={() => document.getElementById('photo-upload').click()}>
              📷
            </PhotoUploadOverlay>
            <FileInput
              id="photo-upload"
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
            />
          </ProfilePhotoSection>
          
          <ProfileDetails>
            <ProfileName>{userData?.firstName} {userData?.lastName}</ProfileName>
            <ProfileUsername>@{userData?.username}</ProfileUsername>
            <ProfileBio>{userData?.bio}</ProfileBio>
            
            <ProfileStats>
              <StatItem>
                <StatNumber>{userData?.connectedAccounts?.length || 0}</StatNumber>
                <StatLabel>Connected Accounts</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>2</StatNumber>
                <StatLabel>Active Calls</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>15</StatNumber>
                <StatLabel>Messages Sent</StatLabel>
              </StatItem>
            </ProfileStats>
          </ProfileDetails>
        </ProfileInfo>
      </ProfileHeader>

      <ProfileContent>
        <MainContent>
          <ProfileSection>
            <SectionTitle>📋 Account Information</SectionTitle>
            <InfoGrid>
              <InfoRow>
                <InfoLabel>Full Name</InfoLabel>
                <InfoValue>{userData?.firstName} {userData?.lastName}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Username</InfoLabel>
                <InfoValue>@{userData?.username}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>{userData?.email}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Phone Number</InfoLabel>
                <InfoValue>{userData?.phoneNumber}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Location</InfoLabel>
                <InfoValue>{userData?.location}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Account Type</InfoLabel>
                <InfoValue style={{ textTransform: 'capitalize' }}>{userData?.accountType}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Member Since</InfoLabel>
                <InfoValue>{new Date(userData?.createdAt).toLocaleDateString()}</InfoValue>
              </InfoRow>
            </InfoGrid>
          </ProfileSection>

          <ProfileSection>
            <SectionTitle>🔗 Connected Accounts</SectionTitle>
            <ConnectedAccounts>
              {userData?.connectedAccounts?.map(account => (
                <AccountCard key={account.id}>
                  <AccountName>{account.inmateName}</AccountName>
                  <AccountDetails>
                    <div>ID: {account.inmateId}</div>
                    <div>{account.facilityName}, {account.state}</div>
                    <div>Relationship: {account.relationship}</div>
                    <div style={{ marginTop: '0.5rem' }}>
                      <StatusBadge className={account.status}>{account.status}</StatusBadge>
                      <span style={{ marginLeft: '1rem', color: '#00ff88' }}>
                        Balance: ${account.balance.toFixed(2)}
                      </span>
                    </div>
                  </AccountDetails>
                </AccountCard>
              ))}
            </ConnectedAccounts>
          </ProfileSection>
        </MainContent>

        <Sidebar>
          <ProfileSection>
            <SectionTitle>⚙️ Quick Actions</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <ActionButton to="/edit-profile">Edit Profile</ActionButton>
              <ActionButton to="/load-money">Load Money</ActionButton>
              <ActionButton to="/inmate-signup">Add Inmate Account</ActionButton>
              <ActionButton to="/settings">Settings</ActionButton>
              <ActionButton to="/support">Contact Support</ActionButton>
            </div>
          </ProfileSection>

          <ProfileSection>
            <SectionTitle>📊 Account Stats</SectionTitle>
            <InfoGrid>
              <InfoRow>
                <InfoLabel>Total Balance</InfoLabel>
                <InfoValue style={{ color: '#00ff88' }}>
                  ${userData?.connectedAccounts?.reduce((sum, acc) => sum + acc.balance, 0).toFixed(2)}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Active Accounts</InfoLabel>
                <InfoValue>
                  {userData?.connectedAccounts?.filter(acc => acc.status === 'active').length}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Pending Accounts</InfoLabel>
                <InfoValue>
                  {userData?.connectedAccounts?.filter(acc => acc.status === 'pending').length}
                </InfoValue>
              </InfoRow>
            </InfoGrid>
          </ProfileSection>

          <ProfileSection>
            <SectionTitle>🔒 Security</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <EditButton>Change Password</EditButton>
              <EditButton>Two-Factor Auth</EditButton>
              <EditButton>Privacy Settings</EditButton>
            </div>
          </ProfileSection>
        </Sidebar>
      </ProfileContent>
    </ProfileContainer>
  );
};

export default Profile; 